import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ListFilter, X, AlertCircle, Plus } from 'lucide-react';

const ApiHeadersInput = ({ headers = {}, onChange }) => {
  const [currentKey, setCurrentKey] = useState('');
  const [currentValue, setCurrentValue] = useState('');
  const [error, setError] = useState('');

  const handleAddHeader = () => {
    if (!currentKey.trim()) {
      setError('Header name cannot be empty');
      return;
    }

    // Create new headers object with the new header
    const newHeaders = {
      ...headers,
      [currentKey.trim()]: currentValue.trim()
    };

    onChange(newHeaders);
    setCurrentKey('');
    setCurrentValue('');
    setError('');
  };

  const handleRemoveHeader = (key) => {
    const newHeaders = { ...headers };
    delete newHeaders[key];
    onChange(newHeaders);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && currentKey.trim()) {
      e.preventDefault();
      handleAddHeader();
    }
  };

  return (
    <div className="space-y-2">
      <div className="p-2 sm:p-3 border rounded-xl bg-gray-50 border-gray-200">
        {/* Headers list */}
        {Object.keys(headers).length > 0 ? (
          <div className="mb-3">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm text-[10px] sm:text-xs overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-1.5 px-2 sm:px-3 text-gray-700 font-medium">Header</th>
                    <th className="text-left py-1.5 px-2 sm:px-3 text-gray-700 font-medium">Value</th>
                    <th className="w-10 py-1.5 px-2 text-gray-700 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {Object.entries(headers).map(([key, value], index) => (
                      <motion.tr
                        key={key}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 10 }}
                        transition={{ duration: 0.2 }}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="py-1.5 px-2 sm:px-3 text-gray-800 font-medium">{key}</td>
                        <td className="py-1.5 px-2 sm:px-3 text-gray-600 truncate max-w-[150px]">{value}</td>
                        <td className="py-1.5 px-2 text-right">
                          <button
                            type="button"
                            onClick={() => handleRemoveHeader(key)}
                            className="text-gray-400 hover:text-gray-600 focus:outline-none p-0.5 rounded-full hover:bg-gray-100"
                          >
                            <X className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-3 text-xs text-gray-400">
            No headers added yet
          </div>
        )}

        {/* Input for new header */}
        <div className="grid grid-cols-5 gap-2">
          <div className="col-span-2">
            <input
              type="text"
              value={currentKey}
              onChange={(e) => {
                setCurrentKey(e.target.value);
                if (error) setError('');
              }}
              onKeyDown={handleKeyDown}
              placeholder="Header name"
              className="w-full border border-gray-200 rounded-lg h-9 sm:h-10 px-3 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400"
            />
          </div>
          <div className="col-span-2">
            <input
              type="text"
              value={currentValue}
              onChange={(e) => setCurrentValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Value"
              className="w-full border border-gray-200 rounded-lg h-9 sm:h-10 px-3 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400"
            />
          </div>
          <div>
            <button
              type="button"
              onClick={handleAddHeader}
              disabled={!currentKey.trim()}
              className={`w-full h-9 sm:h-10 rounded-lg text-xs sm:text-sm font-medium flex items-center justify-center ${
                !currentKey.trim()
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-purple-500 text-white hover:bg-purple-600"
              }`}
            >
              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
              Add
            </button>
          </div>
        </div>

        {/* Error message */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-2 flex items-start text-red-500 text-[11px] sm:text-xs"
            >
              <AlertCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ApiHeadersInput; 